import React, { useEffect, useState } from "react";
import { Copy, Send2, Trash } from "iconsax-reactjs";
import getInitialsMessage from "../../utilities/messages/getInitialsMessage";
import { copyField } from "../../utilities/clipboard";
import DeleteModal from "../DeleteModal";
import { fetchTransferFees, handleDeleteBeneficiary } from "../../api/fetchers";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    setBankRecipient,
    setBankAmount,
    setBankCurrency,
} from "../../redux/slices/bankTransferSlice";
import { setUsdAll, setUsdBeneficiaryId } from "../../redux/slices/usdTransferSlice";
import {
    normalizeBankRecipient,
    normalizeUsdRecipientForStore,
} from "../../utilities/normalizeBeneficiaries";
import SendMoneyModal from "./SendMoneyBeneficiaryModal";

function DetailRow({ label, value, copyable = false }) {
    if (!value && value !== 0) return null;
    return (
        <div className="flex justify-between items-start gap-3">
            <dt className="text-sm text-gray-500 shrink-0">{label}</dt>
            <dd className="text-sm text-[#101828] flex items-center !mb-0 gap-2 text-right break-all">
                {value}
                {copyable && (
                    <button
                        className="p-1 rounded shrink-0"
                        onClick={() => copyField(String(value), label)}
                    >
                        <Copy size="16" color="#6F739B" />
                    </button>
                )}
            </dd>
        </div>
    );
}

const ACCOUNT_TYPE_LABELS = {
    us: "US ACH",
    iban: "IBAN",
    international_wire: "SWIFT / Wire",
    swift: "SWIFT / Wire",
};

export default function BeneficiaryDetailsDrawer({ beneficiary, onClose, onDeleted, businessId }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { business, currency: defaults } = useSelector((s) => s);
    const { name, bankName, accountNumber, currency: benCurrency } = beneficiary;
    const isUsd = String(benCurrency || defaults || "").toUpperCase() === "USD";

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [feeLoading, setFeeLoading] = useState(false);
    const [fxFeeRate, setFxFeeRate] = useState(0);

    const confirmDelete = async () => {
        setIsDeleting(true);
        try {
            await handleDeleteBeneficiary({
                businessId,
                beneficiaryId: beneficiary._id,
            });
            toast.success("Beneficiary deleted");
            onDeleted?.(beneficiary._id);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.message ?? "Failed to delete beneficiary");
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    useEffect(() => {
        if (!business?._id) return;
        let cancelled = false;
        (async () => {
            try {
                setFeeLoading(true);
                const fees = await fetchTransferFees({ businessId: business._id });
                const fx = Number(fees?.fxTransferFee ?? fees?.data?.fxTransferFee ?? 0);
                if (!cancelled) setFxFeeRate(Number.isFinite(fx) ? fx : 0);
            } catch {
                if (!cancelled) setFxFeeRate(0);
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [business?._id]);

    const meta = beneficiary?.meta || {};
    const accountType = meta.account_type || beneficiary?.accountType || "";
    const accountTypeLabel = ACCOUNT_TYPE_LABELS[accountType] || accountType;
    const ownerType = meta.account_owner_type || beneficiary?.accountOwnerType || "";
    const ownerName = meta.account_owner_name || beneficiary?.accountOwnerName || name || "";
    const firstName = meta.first_name || beneficiary?.firstName || "";
    const lastName = meta.last_name || beneficiary?.lastName || "";
    const businessNameField = meta.business_name || beneficiary?.businessName || "";
    const email = meta.email || beneficiary?.email || "";
    const relationship = meta.relationship || beneficiary?.relationship || "";

    // US ACH account/routing
    const acct = meta.account || beneficiary?.account || {};
    const accountNum = acct.account_number || acct.accountNumber || accountNumber || "";
    const routingNumber = acct.routing_number || acct.routingNumber || "";
    const checkingOrSavings = acct.checking_or_savings || acct.checkingOrSavings || "";

    // IBAN
    const ibanBlock = meta.iban || beneficiary?.iban || {};
    const ibanNumber = ibanBlock.account_number || ibanBlock.iban || "";
    const ibanBic = ibanBlock.bic || meta.bic || beneficiary?.bic || "";
    const ibanCountry = ibanBlock.country || "";

    // SWIFT
    const swiftBlock = meta.swift || beneficiary?.swift || {};
    const swiftAccountNum = swiftBlock.account_number || swiftBlock.accountNumber || accountNumber || "";
    const swiftBic = swiftBlock.bic || meta.bic || beneficiary?.bic || "";
    const swiftCountry = swiftBlock.country || "";

    // Recipient address
    const address = meta.address || beneficiary?.address || {};
    const street = address.street_line_1 || address.streetLine1 || "";
    const city = address.city || "";
    const state = address.state || "";
    const postalCode = address.postal_code || address.postalCode || "";
    const country = address.country || "";

    // Bank address (US ACH)
    const bankAddressBlock = meta.bank_address || beneficiary?.bankAddress || {};
    const bankStreet = bankAddressBlock.street_line_1 || bankAddressBlock.streetLine1 || "";
    const bankCity = bankAddressBlock.city || "";
    const bankState = bankAddressBlock.state || "";
    const bankPostal = bankAddressBlock.postal_code || bankAddressBlock.postalCode || "";
    const bankCountry = bankAddressBlock.country || "";

    /* ── Send money handler ───────────────────────────────── */
    const handleSendContinue = ({ amount, currency, beneficiary: b, rateKey }) => {
        const cur = String(currency || defaults || "").toUpperCase();
        const amt = String(amount);

        if (["USDT", "USDC"].includes(cur)) {
            dispatch({ type: "cryptoFiatTransfer/setCryptoFiatAmount", payload: amt });
            dispatch({
                type: "cryptoFiatTransfer/setCryptoFiatRecipient",
                payload: {
                    institution: b.bankCode,
                    accountIdentifier: b.accountNumber,
                    accountName: b.name,
                    bankName: b.bankName,
                    toCurrency: b.currency ?? "NGN",
                },
            });
            onClose();
            navigate("/payment/send-crypto/fiat-wallet/payment-info");
            return;
        }

        if (cur === "USD") {
            const bridgeId = b?.bridgeBeneficiaryId || b?.beneficiaryId || "";
            const listId = b?._id || "";
            dispatch(setUsdBeneficiaryId(listId || bridgeId));
            const usdPayload = normalizeUsdRecipientForStore(b);
            dispatch(
                setUsdAll({
                    amount: amt,
                    currency: "USD",
                    rateKey: String(rateKey || ""),
                    beneficiaryId: listId || bridgeId,
                    transactionId: "",
                    bridgeBeneficiaryId: bridgeId,
                    ...usdPayload,
                })
            );
            onClose();
            navigate("/payment/send-money/same-currency/bank-recipient/payment-info");
            return;
        }

        // NGN / EUR / GBP / KES / etc.
        const bankRecipientPayload = normalizeBankRecipient(b);
        dispatch(setBankRecipient(bankRecipientPayload));
        dispatch(setBankAmount(amt));
        dispatch(setBankCurrency(cur));
        onClose();
        navigate("/payment/send-money/same-currency/bank-recipient/payment-info");
    };

    return (
        <>
            {/* backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-25 z-[800]" onClick={onClose} />

            {/* drawer panel */}
            <aside className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-lg z-[900] flex flex-col">
                {/* header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-4">
                        <div className="h-14 w-14 rounded-full bg-[#F4F2FC] flex items-center justify-center text-xl font-normal text-[#101828]">
                            {getInitialsMessage(name)}
                        </div>
                        <div>
                            <h2 className="text-lg !mb-0 font-semibold text-[#101828]">{name}</h2>
                            {benCurrency && (
                                <span className="text-xs text-[#6F739B] font-medium uppercase">
                                    {benCurrency} transfer
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        ✕
                    </button>
                </div>

                {/* account details */}
                <div className="p-6 flex-1 overflow-y-auto space-y-6">

                    {/* ── Non-USD (standard) details ── */}
                    {!isUsd && (
                        <section>
                            <h3 className="mb-2 text-sm font-semibold text-gray-700">Account details</h3>
                            <dl className="bg-[#F9FAFF] rounded-lg p-3 grid gap-y-3">
                                <DetailRow label="Bank name" value={bankName} copyable />
                                <DetailRow label="Account Number" value={accountNumber} copyable />
                                <DetailRow label="Account Name" value={name} copyable />
                            </dl>
                        </section>
                    )}

                    {isUsd && (
                        <>
                            {/* Account holder */}
                            <section>
                                <h3 className="mb-2 text-sm font-semibold text-gray-700">Account holder</h3>
                                <dl className="bg-[#F9FAFF] rounded-lg p-3 grid gap-y-3">
                                    <DetailRow label="Account name" value={ownerName} copyable />
                                    {ownerType && (
                                        <DetailRow
                                            label="Holder type"
                                            value={ownerType === "corporate" ? "Corporate / Business" : "Individual"}
                                        />
                                    )}
                                    {firstName && <DetailRow label="First name" value={firstName} copyable />}
                                    {lastName && <DetailRow label="Last name" value={lastName} copyable />}
                                    {businessNameField && (
                                        <DetailRow label="Business name" value={businessNameField} copyable />
                                    )}
                                    {email && <DetailRow label="Email" value={email} copyable />}
                                    {relationship && <DetailRow label="Relationship" value={relationship} />}
                                </dl>
                            </section>

                            {/* Payment method */}
                            {accountTypeLabel && (
                                <section>
                                    <h3 className="mb-2 text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        Payment method
                                        <span className="px-2 py-0.5 rounded-full bg-[#EBE4FF] text-[#5E01D6] text-xs font-medium">
                                            {accountTypeLabel}
                                        </span>
                                    </h3>
                                    <dl className="bg-[#F9FAFF] rounded-lg p-3 grid gap-y-3">
                                        {/* US ACH */}
                                        {accountType === "us" && (
                                            <>
                                                <DetailRow label="Bank name" value={bankName} copyable />
                                                <DetailRow label="Account number" value={accountNum} copyable />
                                                <DetailRow label="Routing number" value={routingNumber} copyable />
                                                {checkingOrSavings && (
                                                    <DetailRow
                                                        label="Account type"
                                                        value={
                                                            checkingOrSavings.charAt(0).toUpperCase() +
                                                            checkingOrSavings.slice(1)
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                        {/* IBAN */}
                                        {accountType === "iban" && (
                                            <>
                                                <DetailRow label="Bank name" value={bankName} copyable />
                                                <DetailRow label="IBAN / Account number" value={ibanNumber} copyable />
                                                <DetailRow label="SWIFT / BIC" value={ibanBic} copyable />
                                                <DetailRow label="Bank country" value={ibanCountry} />
                                            </>
                                        )}
                                        {/* SWIFT / Wire */}
                                        {(accountType === "international_wire" || accountType === "swift") && (
                                            <>
                                                <DetailRow label="Bank name" value={bankName} copyable />
                                                <DetailRow label="Account number" value={swiftAccountNum} copyable />
                                                <DetailRow label="SWIFT / BIC" value={swiftBic} copyable />
                                                <DetailRow label="Bank country" value={swiftCountry} />
                                            </>
                                        )}
                                    </dl>
                                </section>
                            )}

                            {/* Bank address (US ACH only) */}
                            {accountType === "us" && bankStreet && (
                                <section>
                                    <h3 className="mb-2 text-sm font-semibold text-gray-700">Bank address</h3>
                                    <dl className="bg-[#F9FAFF] rounded-lg p-3 grid gap-y-3">
                                        <DetailRow label="Street" value={bankStreet} copyable />
                                        <DetailRow label="City" value={bankCity} />
                                        <DetailRow label="State" value={bankState} />
                                        <DetailRow label="Postal code" value={bankPostal} copyable />
                                        <DetailRow label="Country" value={bankCountry} />
                                    </dl>
                                </section>
                            )}

                            {/* Recipient address */}
                            {street && (
                                <section>
                                    <h3 className="mb-2 text-sm font-semibold text-gray-700">Recipient address</h3>
                                    <dl className="bg-[#F9FAFF] rounded-lg p-3 grid gap-y-3">
                                        <DetailRow label="Street" value={street} copyable />
                                        <DetailRow label="City" value={city} />
                                        <DetailRow label="State" value={state} />
                                        <DetailRow label="Postal code" value={postalCode} copyable />
                                        <DetailRow label="Country" value={country} />
                                    </dl>
                                </section>
                            )}
                        </>
                    )}
                </div>

                {/* actions */}
                <div className="p-6 border-t flex gap-4">
                    <button
                        className="flex-1 py-2 bg-[#EBE4FF] text-[#5E01D6] rounded-lg flex items-center justify-center gap-2"
                        onClick={() => setShowSendModal(true)}
                    >
                        <Send2 size={20} /> Send money
                    </button>
                    <button
                        className="flex-1 py-2 bg-[#FFEDEC] text-[#DC1F28] rounded-lg flex items-center justify-center gap-2"
                        onClick={() => setShowDeleteModal(true)}
                    >
                        <Trash size={20} /> Delete beneficiary
                    </button>
                </div>

                <DeleteModal
                    isOpen={showDeleteModal}
                    title="Delete beneficiary?"
                    description={`You are about to delete ${name} from your beneficiaries. This action cannot be undone.`}
                    confirmText="Delete beneficiary"
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={confirmDelete}
                    isLoading={isDeleting}
                />

                <SendMoneyModal
                    isOpen={showSendModal}
                    onClose={() => setShowSendModal(false)}
                    beneficiary={beneficiary}
                    currency={defaults}
                    transferFeeRate={fxFeeRate}
                    feeLoading={feeLoading}
                    showCurrencySymbol={!["USDT", "USDC"].includes(defaults)}
                    onContinue={handleSendContinue}
                />
            </aside>
        </>
    );
}
